import React, {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  Fragment,
} from 'react'
import { Button, LabeledInput, Select } from '@apex/component-library'
import {
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Icon,
  Text,
} from '@chakra-ui/react'
import { FaTrashAlt } from 'react-icons/fa'
import { v4 as uuid } from 'uuid'
import {} from '../../../api/types'
import { DatePickerInput } from '@apex/component-library'
import {
  OptionsError,
  OptionsTouched,
  OptionsValueProperty,
  OptionsValueTypes,
  OptionsValueWithRowId,
} from './types'
import { FieldsEnum, PositionEnum, PutCallEnum } from './constants'
import AmountInput from './AmountInput'
import { getToday } from '../../../utils'

// Blueprint's DatePicker caps the calendar at 6 months out by default, and
// listed LEAPS expire years away (ACCM-6775)
const CONTRACT_DATE_MAX_YEARS_OUT = 10

export interface OptionsFormProps {
  errors?: OptionsError[]
  options?: OptionsValueWithRowId[]
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
}

type OptionAction = {
  type: 'ADD' | 'REMOVE' | 'UPDATE'
  rowId?: string
  value?: OptionsValueTypes
  field?: OptionsValueProperty
}

const reducer: Reducer<OptionsValueWithRowId[], OptionAction> = (
  state: OptionsValueWithRowId[],
  action: OptionAction
) => {
  switch (action.type) {
    case 'ADD':
      return [
        ...state,
        {
          rowId: uuid(),
          symbol: undefined,
          contractDate: undefined,
          put: undefined,
          strikePrice: undefined,
          isShort: undefined,
          amount: undefined,
        },
      ]
    case 'REMOVE':
      return state.filter(item => item.rowId !== action.rowId)
    case 'UPDATE':
      return state.map(item => {
        if (item.rowId === action.rowId && action.field) {
          return { ...item, [action.field]: action.value }
        }
        return item
      })
    default:
      return state
  }
}

const OptionsForm = ({ errors, options, setFieldValue }: OptionsFormProps) => {
  const [optionsList, dispatch] = useReducer(reducer, options || [])
  const [fieldTouched, setFieldTouched] = useState<OptionsTouched>({})

  const setFieldValueMemo = useCallback(setFieldValue, [])
  const contractDateMaxDate = useMemo(
    () => getToday().plus({ years: CONTRACT_DATE_MAX_YEARS_OUT }).toJSDate(),
    []
  )

  useEffect(() => {
    setFieldValueMemo('options', optionsList)
  }, [optionsList, setFieldValueMemo])

  const getError = (rowId: string, fieldName: OptionsValueProperty) => {
    const rowIndex = optionsList.findIndex(option => option.rowId === rowId)
    const rowErrors = errors ? errors[rowIndex] : undefined
    const fieldError =
      rowErrors &&
      fieldTouched[rowId] &&
      fieldTouched[rowId][fieldName] &&
      rowErrors[fieldName]
    return fieldError ? fieldError : undefined
  }

  const handleBlur = (rowId: string, fieldName: string) =>
    setFieldTouched(previous => ({
      ...previous,
      [rowId]: {
        ...previous[rowId],
        [fieldName]: true,
      },
    }))

  const titleSpan = 4
  const positionSpan = 2
  const symbolSpan = 4
  const amountSpan = 2
  const dateSpan = 3
  const priceSpan = 3
  const putSpan = 2
  const trashSpan = 1 // Should bring totalColumnSpan up to 21 for form consistency
  const totalColumnSpan =
    titleSpan +
    positionSpan +
    symbolSpan +
    amountSpan +
    dateSpan +
    priceSpan +
    putSpan +
    trashSpan

  return (
    <Grid
      borderBottom="1px solid gray"
      gridGap={4}
      padding={'1rem 0 1rem 1rem'}
      templateRows={`repeat(${optionsList?.length}, fit-content 1fr)`}
      templateColumns={`repeat(${totalColumnSpan}, 1fr)`}
    >
      <GridItem colSpan={titleSpan} rowSpan={optionsList?.length! + 1}>
        <Text fontWeight="semibold">Options Transfer</Text>
        <Button
          data-testid="options-form__add-button"
          mt={5}
          variant="link"
          onClick={() => dispatch({ type: 'ADD' })}
        >
          + Add Option
        </Button>
      </GridItem>
      <GridItem colSpan={positionSpan} hidden={!optionsList?.length}>
        <Text fontWeight="semibold">Position*</Text>
      </GridItem>
      <GridItem colSpan={symbolSpan} hidden={!optionsList?.length}>
        <Text fontWeight="semibold">Symbol*</Text>
      </GridItem>
      <GridItem
        colSpan={amountSpan}
        justifySelf="end"
        hidden={!optionsList?.length}
      >
        <Text fontWeight="semibold">Quantity*</Text>
      </GridItem>
      <GridItem colSpan={dateSpan} hidden={!optionsList?.length}>
        <Text fontWeight="semibold">Expiration Date*</Text>
      </GridItem>
      <GridItem
        colSpan={priceSpan}
        justifySelf="end"
        hidden={!optionsList?.length}
      >
        <Text fontWeight="semibold">Strike Price*</Text>
      </GridItem>
      <GridItem colSpan={putSpan + trashSpan} hidden={!optionsList?.length}>
        <Text fontWeight="semibold">Put/Call*</Text>
      </GridItem>
      {optionsList?.map(
        ({
          rowId,
          symbol,
          contractDate,
          put,
          strikePrice,
          isShort,
          amount,
        }: OptionsValueWithRowId) => {
          const shortDisplayString =
            isShort === undefined ? '' : isShort ? 'Short' : 'Long'

          return (
            <Fragment key={rowId}>
              <GridItem colSpan={positionSpan} data-testid="options-form__row">
                <FormControl isInvalid={!!getError(rowId!, FieldsEnum.IsShort)}>
                  <Select
                    data-testid="options-form__isShort"
                    value={shortDisplayString}
                    onBlur={() => handleBlur(rowId!, FieldsEnum.IsShort)}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value === 'Short',
                        rowId,
                        field: FieldsEnum.IsShort,
                      })
                    }
                  >
                    <option style={{ display: 'none' }} />
                    <option value={PositionEnum.Short}>
                      {PositionEnum.Short}
                    </option>
                    <option value={PositionEnum.Long}>
                      {PositionEnum.Long}
                    </option>
                  </Select>
                  <FormErrorMessage>
                    {getError(rowId!, FieldsEnum.IsShort)}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={symbolSpan}>
                <FormControl isInvalid={!!getError(rowId!, FieldsEnum.Symbol)}>
                  <LabeledInput
                    data-testid="options-form__symbol"
                    value={symbol || ''}
                    isInvalid={!!getError(rowId!, FieldsEnum.Symbol)}
                    label=""
                    type="text"
                    onBlur={() => handleBlur(rowId!, FieldsEnum.Symbol)}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value || undefined,
                        rowId,
                        field: FieldsEnum.Symbol,
                      })
                    }
                  />
                  <FormErrorMessage>
                    {getError(rowId!, FieldsEnum.Symbol)}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={amountSpan} justifySelf="end">
                <FormControl isInvalid={!!getError(rowId!, FieldsEnum.Amount)}>
                  <LabeledInput
                    data-testid="options-form__amount"
                    value={amount || ''}
                    isInvalid={!!getError(rowId!, FieldsEnum.Amount)}
                    label=""
                    type="number"
                    textAlign="right"
                    onBlur={() => handleBlur(rowId!, FieldsEnum.Amount)}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value || undefined,
                        rowId,
                        field: FieldsEnum.Amount,
                      })
                    }
                  />
                  <FormErrorMessage>
                    {getError(rowId!, FieldsEnum.Amount)}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={dateSpan}>
                <FormControl
                  isInvalid={!!getError(rowId!, FieldsEnum.ContractDate)}
                >
                  <DatePickerInput
                    date={contractDate || ''}
                    maxDate={contractDateMaxDate}
                    isInvalid={!!getError(rowId!, FieldsEnum.ContractDate)}
                    onBlur={() => handleBlur(rowId!, FieldsEnum.ContractDate)}
                    onChange={date => {
                      dispatch({
                        type: 'UPDATE',
                        value: date as string,
                        rowId,
                        field: FieldsEnum.ContractDate,
                      })
                    }}
                  />
                  <FormErrorMessage>
                    {getError(rowId!, FieldsEnum.ContractDate)}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={priceSpan} justifySelf="end">
                <FormControl
                  isInvalid={!!getError(rowId!, FieldsEnum.StrikePrice)}
                >
                  <AmountInput
                    rowId={rowId || ''}
                    value={strikePrice}
                    error={getError(rowId!, FieldsEnum.StrikePrice)}
                    onBlur={() => handleBlur(rowId!, FieldsEnum.StrikePrice)}
                    handleChange={value =>
                      dispatch({
                        type: 'UPDATE',
                        value,
                        rowId,
                        field: FieldsEnum.StrikePrice,
                      })
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={putSpan}>
                <FormControl isInvalid={!!getError(rowId!, FieldsEnum.Put)}>
                  <Select
                    data-testid="options-form__put"
                    value={put || ''}
                    onBlur={() => handleBlur(rowId!, FieldsEnum.Put)}
                    onChange={e =>
                      dispatch({
                        type: 'UPDATE',
                        value: e.target.value || undefined,
                        rowId,
                        field: FieldsEnum.Put,
                      })
                    }
                    sx={{ textTransform: 'capitalize' }}
                  >
                    <option style={{ display: 'none' }} />
                    <option
                      value={PutCallEnum.Put}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {PutCallEnum.Put.toLowerCase()}
                    </option>
                    <option
                      value={PutCallEnum.Call}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {PutCallEnum.Call.toLowerCase()}
                    </option>
                  </Select>
                  <FormErrorMessage>
                    {getError(rowId!, FieldsEnum.Put)}
                  </FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={trashSpan} justifySelf="end">
                <Button
                  data-testid="options-form__delete-button"
                  onClick={() => dispatch({ type: 'REMOVE', rowId })}
                  variant="link"
                >
                  <Icon as={FaTrashAlt} color="gray.400" h={6} w={6} />
                </Button>
              </GridItem>
            </Fragment>
          )
        }
      )}
    </Grid>
  )
}

export default OptionsForm
